import React, {useState} from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  Checkbox,
  FormControlLabel,
  Grid,
} from '@mui/material';
import {useSaveProductionLocation} from '../../../hooks/productions/useProductions';
import {SaveProductionLocationRequestBody} from '../../../api/types/production.type';
import '../Demo.scss';

const SaveProductionLocationApiTest: React.FC = () => {
  // Basic Info
  const [productionId, setProductionId] = useState<number>(235);
  const [isMarkComplete, setIsMarkComplete] = useState<boolean>(false);
  const [moduleType, setModuleType] = useState<string>('PRODUCTION');
  const [securityGroupId, setSecurityGroupId] = useState<number>(1);

  // Production Output Location
  const [productionRootPathID, setProductionRootPathID] = useState<number>(2);
  const [productionDirectory, setProductionDirectory] =
    useState<string>('apiissuefolder5');
  const [shouldIncludeDrive, setShouldIncludeDrive] = useState<boolean>(true);
  const [driveText, setDriveText] = useState<string>('j:\\');
  const [isVolumeIncluded, setIsVolumeIncluded] = useState<boolean>(true);
  const [volumeName, setVolumeName] = useState<string>('abc');
  const [volumeLocationID, setVolumeLocationID] =
    useState<string>('In All Folders');
  const [shouldArchive, setShouldArchive] = useState<boolean>(false);
  const [productionRootPathName, setProductionRootPathName] = useState<string>(
    '\\\\devnas10.consiliotest.com\\AURORA_REVIEW_DEV4\\Productions\\dndproject8'
  );

  // Production Components Folder Details
  const [folderName_Natives, setFolderName_Natives] =
    useState<string>('Natives');
  const [folderName_PDF, setFolderName_PDF] = useState<string>('PDF');
  const [folderName_Text, setFolderName_Text] = useState<string>('Text');
  const [folderName_MP3, setFolderName_MP3] = useState<string>('MP3 Files');
  const [folderName_Images, setFolderName_Images] = useState<string>('Images');
  const [folderName_LoadFiles, setFolderName_LoadFiles] =
    useState<string>('Load Files');
  const [folderName_Translations, setFolderName_Translations] =
    useState<string>('Translations');
  const [toSplitSubFolders, setToSplitSubFolders] = useState<boolean>(false);
  const [splitCount, setSplitCount] = useState<number>(1000);

  const [saveResult, setSaveResult] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<boolean>(false);

  const saveMutation = useSaveProductionLocation(
    (data) => {
      setSaveResult(`✅ ${data.message}`);
    },
    (error) => {
      setSaveResult(`❌ Save failed: ${error.message}`);
    }
  );

  const handleSave = () => {
    const requestBody: SaveProductionLocationRequestBody = {
      ProductionId: productionId,
      IsMarkComplete: isMarkComplete,
      ModuleType: moduleType,
      ProductionOutputLocation: {
        ProductionRootPathID: productionRootPathID,
        ProductionDirectory: productionDirectory,
        ShouldIncludeDrive: shouldIncludeDrive,
        driveText: driveText,
        IsVolumeIncluded: isVolumeIncluded,
        VolumeName: volumeName,
        VolumeLocationID: volumeLocationID,
        ShouldArchive: shouldArchive,
        ProductionRootPathName: productionRootPathName,
      },
      ProductionComponentsFolderDetails: {
        FolderName_Natives: folderName_Natives,
        FolderName_PDF: folderName_PDF,
        FolderName_Text: folderName_Text,
        FolderName_MP3: folderName_MP3,
        FolderName_Images: folderName_Images,
        FolderName_LoadFiles: folderName_LoadFiles,
        FolderName_Translations: folderName_Translations,
        ToSplitSubFolders: toSplitSubFolders,
        SplitCount: splitCount,
      },
      SecurityGroupId: securityGroupId,
    };
    saveMutation.mutate(requestBody);
  };

  const handleReset = () => {
    setSaveResult('');
  };

  const handleCopyJson = async () => {
    if (saveMutation.data) {
      try {
        await navigator.clipboard.writeText(
          JSON.stringify(saveMutation.data, null, 2)
        );
        setCopySuccess(true);
        setTimeout(() => setCopySuccess(false), 2000);
      } catch (err) {
        console.error('Failed to copy:', err);
      }
    }
  };

  return (
    <Box className="demo-page" sx={{padding: 3}}>
      <Box sx={{marginBottom: 4}}>
        <Typography
          variant="h4"
          className="typo-text headline1_size"
          gutterBottom
        >
          🧪 Save Production Location API Test
        </Typography>
        <Typography className="typo-text body2_size" sx={{color: '#666'}}>
          Save production location settings including output location, folder
          details, and component configurations.
        </Typography>
      </Box>

      {/* API Info */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              marginBottom: 2,
            }}
          >
            <Chip label="POST" color="success" size="small" data-testid={`save-production-location-chip-info`} />
            <Typography variant="h6" className="typo-text subhead2_size">
              Save Production Location
            </Typography>
          </Box>
          <Typography
            className="typo-text body3_size"
            sx={{
              fontFamily: 'monospace',
              backgroundColor: '#f5f5f5',
              padding: '4px 8px',
              borderRadius: '4px',
              display: 'inline-block',
              marginBottom: 1,
              fontSize: '12px',
            }}
          >
            /productions/api/v1/production/location
          </Typography>
          <Typography className="typo-text body3_size" sx={{color: '#666'}}>
            Save production location settings with output location
            configuration, folder details, and component settings. Returns a
            success message.
          </Typography>
        </CardContent>
      </Card>

      {/* Request Body */}
      <Card sx={{marginBottom: 3}}>
        <CardContent>
          <Typography
            variant="h6"
            className="typo-text subhead3_size"
            gutterBottom
          >
            Request Body
          </Typography>

          {/* Basic Information */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Basic Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Production ID"
                  type="number"
                  value={productionId}
                  onChange={(e) => setProductionId(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Module Type"
                  value={moduleType}
                  onChange={(e) => setModuleType(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Security Group ID"
                  type="number"
                  value={securityGroupId}
                  onChange={(e) => setSecurityGroupId(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControlLabel
                data-testid="is-mark-complete-form-control-label-save-production"
                  control={
                    <Checkbox
                      data-testid="save-production-location-api-test-is-mark-complete"
                      checked={isMarkComplete}
                      onChange={(e) => setIsMarkComplete(e.target.checked)}
                    />
                  }
                  label="Is Mark Complete"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Production Output Location */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Production Output Location
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production Root Path ID"
                  type="number"
                  value={productionRootPathID}
                  onChange={(e) =>
                    setProductionRootPathID(Number(e.target.value))
                  }
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production Directory"
                  value={productionDirectory}
                  onChange={(e) => setProductionDirectory(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Production Root Path Name"
                  value={productionRootPathName}
                  onChange={(e) => setProductionRootPathName(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Drive Text"
                  value={driveText}
                  onChange={(e) => setDriveText(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Volume Name"
                  value={volumeName}
                  onChange={(e) => setVolumeName(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Volume Location ID"
                  value={volumeLocationID}
                  onChange={(e) => setVolumeLocationID(e.target.value)}
                  size="small"
                  fullWidth
                  placeholder="e.g., In All Folders"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="should-include-drive-form-control-label-save-production"
                  control={
                    <Checkbox
                      data-testid="save-production-location-api-test-should-include-drive"
                      checked={shouldIncludeDrive}
                      onChange={(e) => setShouldIncludeDrive(e.target.checked)}
                    />
                  }
                  label="Should Include Drive"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="is-volume-included-form-control-label-save-production"
                  control={
                    <Checkbox
                      data-testid="save-production-location-api-test-is-volume-included"
                      checked={isVolumeIncluded}
                      onChange={(e) => setIsVolumeIncluded(e.target.checked)}
                    />
                  }
                  label="Is Volume Included"
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="should-archive-form-control-label-save-production"
                  control={
                    <Checkbox
                      data-testid="save-production-location-api-test-should-archive"
                      checked={shouldArchive}
                      onChange={(e) => setShouldArchive(e.target.checked)}
                    />
                  }
                  label="Should Archive"
                />
              </Grid>
            </Grid>
          </Box>

          {/* Production Components Folder Details */}
          <Box sx={{marginBottom: 3}}>
            <Typography
              className="typo-text subhead3_size"
              sx={{marginBottom: 2}}
            >
              Production Components Folder Details
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Natives Folder"
                  value={folderName_Natives}
                  onChange={(e) => setFolderName_Natives(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="PDF Folder"
                  value={folderName_PDF}
                  onChange={(e) => setFolderName_PDF(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Text Folder"
                  value={folderName_Text}
                  onChange={(e) => setFolderName_Text(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="MP3 Folder"
                  value={folderName_MP3}
                  onChange={(e) => setFolderName_MP3(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Images Folder"
                  value={folderName_Images}
                  onChange={(e) => setFolderName_Images(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Load Files Folder"
                  value={folderName_LoadFiles}
                  onChange={(e) => setFolderName_LoadFiles(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Translations Folder"
                  value={folderName_Translations}
                  onChange={(e) => setFolderName_Translations(e.target.value)}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="Split Count"
                  type="number"
                  value={splitCount}
                  onChange={(e) => setSplitCount(Number(e.target.value))}
                  size="small"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <FormControlLabel
                data-testid="to-split-sub-folder-form-control-label-save-production"
                  control={
                    <Checkbox
                      data-testid="save-production-location-api-test-to-split-sub-folders"
                      checked={toSplitSubFolders}
                      onChange={(e) => setToSplitSubFolders(e.target.checked)}
                    />
                  }
                  label="To Split Sub Folders"
                />
              </Grid>
            </Grid>
          </Box>

          <Box sx={{display: 'flex', gap: 2}}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? '⏳ Saving...' : '💾 Save Location'}
            </Button>
            <Button variant="outlined" onClick={handleReset}>
              🔄 Reset
            </Button>
            {saveMutation.data && (
              <Button variant="outlined" onClick={handleCopyJson}>
                {copySuccess ? '✅ Copied!' : '📋 Copy JSON'}
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Loading State */}
      {saveMutation.isPending && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="info">
              ⏳ Saving production location... Please wait...
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Error State */}
      {saveMutation.isError && (
        <Card sx={{marginBottom: 3}}>
          <CardContent>
            <Alert severity="error">
              <Typography className="typo-text subhead3_size" gutterBottom>
                ❌ Error
              </Typography>
              <Typography className="typo-text body3_size">
                {saveMutation.error?.message ||
                  'An error occurred while saving production location'}
              </Typography>
            </Alert>
          </CardContent>
        </Card>
      )}

      {/* Success State */}
      {saveMutation.isSuccess && saveMutation.data && (
        <>
          <Card sx={{marginBottom: 3}}>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                Save Result
              </Typography>

              <Alert severity="success" sx={{marginBottom: 2}}>
                <Typography className="typo-text subhead3_size" gutterBottom>
                  ✅ Success
                </Typography>
                <Typography className="typo-text body3_size">
                  {saveMutation.data.message}
                </Typography>
              </Alert>
            </CardContent>
          </Card>

          {/* JSON Response */}
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                className="typo-text subhead3_size"
                gutterBottom
              >
                JSON Response
              </Typography>
              <Box
                sx={{
                  backgroundColor: '#f5f5f5',
                  padding: 2,
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: '12px',
                  maxHeight: 300,
                  overflow: 'auto',
                }}
              >
                <pre>{JSON.stringify(saveMutation.data, null, 2)}</pre>
              </Box>
            </CardContent>
          </Card>
        </>
      )}

      {/* Result Display */}
      {saveResult && !saveMutation.isPending && (
        <Card>
          <CardContent>
            <Typography
              variant="h6"
              className="typo-text subhead3_size"
              gutterBottom
            >
              Save Result
            </Typography>
            <Alert severity={saveResult.includes('✅') ? 'success' : 'error'}>
              {saveResult}
            </Alert>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SaveProductionLocationApiTest;
